import React, { createContext, useState } from 'react';
import { SectionData } from './report/types';
import { SectionResult } from './types';

type SectionResults = Record<string, SectionData | null>;
type CompetitorSectionResults = Record<string, { [competitor: string]: SectionResult | null }>;
type CompetitorData = {
    reportGuid: string;
    websiteUrl: string;
};

type AnalysisInProgressContext = {
    lastAnalysisGuid: string | null;
    lastAnalysisUrl: string | null;
    lastAnalysisSections: string[];
    lastAnalysisCompetitors: CompetitorData[];
    lastAnalysisCreatedAt: string | null;
    isAnalysisInProgress: boolean;
    sectionResults: SectionResults;
    competitorSectionResults: CompetitorSectionResults;
    flushAnalysisData: () => void;
    setLastAnalysisCreatedAt: (createdAt: React.SetStateAction<string | null>) => void;
    setLastAnalysisGuid: (guid: string | null) => void;
    setLastAnalysisUrl: (url: string | null) => void;
    setIsAnalysisInProgress: (status: boolean) => void;
    setSectionResults: (sections: React.SetStateAction<SectionResults>) => void;
    setLastAnalysisSections: (sections: React.SetStateAction<string[]>) => void;
    setLastAnalysisCompetitors: (competitors: React.SetStateAction<CompetitorData[]>) => void;
    setCompetitorSectionResults: (sections: React.SetStateAction<CompetitorSectionResults>) => void;
};

export const AnalysisInProgressContext = createContext<AnalysisInProgressContext>({} as AnalysisInProgressContext);

type Props = {
    children: JSX.Element;
};

export default function AnalysisInProgressContextWrapper({ children }: Props) {
    const [isAnalysisInProgress, setIsAnalysisInProgress] = useState<boolean>(false);
    const [lastAnalysisCreatedAt, setLastAnalysisCreatedAt] = useState<string | null>(null);
    const [lastAnalysisGuid, setLastAnalysisGuid] = useState<string | null>(null);
    const [lastAnalysisUrl, setLastAnalysisUrl] = useState<string | null>(null);
    const [lastAnalysisSections, setLastAnalysisSections] = useState<string[]>([]);
    const [lastAnalysisCompetitors, setLastAnalysisCompetitors] = useState<CompetitorData[]>([]);
    const [sectionResults, setSectionResults] = useState<SectionResults>({});
    const [competitorSectionResults, setCompetitorSectionResults] = useState<CompetitorSectionResults>({});

    const flushAnalysisData = () => {
        setLastAnalysisCreatedAt(null);
        setLastAnalysisGuid(null);
        setLastAnalysisUrl(null);
        setLastAnalysisSections([]);
        setLastAnalysisCompetitors([]);
        setSectionResults({});
        setCompetitorSectionResults({});
    };

    return (
        <AnalysisInProgressContext.Provider
            value={{
                lastAnalysisCreatedAt,
                lastAnalysisGuid,
                lastAnalysisUrl,
                lastAnalysisSections,
                lastAnalysisCompetitors,
                isAnalysisInProgress,
                sectionResults,
                competitorSectionResults,
                setLastAnalysisCreatedAt,
                setLastAnalysisGuid,
                setLastAnalysisUrl,
                setLastAnalysisSections,
                setCompetitorSectionResults,
                setSectionResults,
                setLastAnalysisCompetitors,
                setIsAnalysisInProgress,
                flushAnalysisData,
            }}
        >
            {children}
        </AnalysisInProgressContext.Provider>
    );
}
